import { DateFragment, ShippingAddressFragment } from '@graphql/generated';
import { differenceInCalendarDays } from 'date-fns';

export enum WarningLevel {
  None = 0,
  Low = 1,
  Moderate = 2,
  Severe = 3,
}

export const isTheWeddingSoon = (finalizedEventDate: Maybe<DateFragment>) => {
  if (finalizedEventDate) {
    const today = Date.now();
    const countDown = differenceInCalendarDays(finalizedEventDate.milliseconds, today);
    return countDown <= 60;
  }
  return null;
};

/**
 * **getWarningLevel**
 *
 * The condition to show the warnings is:
 *
 * - No Warning (None): gifts === 0. If no gifts, no make sense show warnings.
 * - Warning Low: gifts > 0.
 * - Warning Moderate: gifts > 10
 * - Warning Severe: < 60 days to their wedding
 * - Warning Severe: Address is not validated
 *
 * @param deliverableProducts number of deliverable products
 * @param finalizedEventDate wedding's date
 * @param registryShippingAddress the couple's shipping address
 * @returns the corresponding warning level: None, Moderate or Severe
 */
export const getWarningLevel = ({
  deliverableProducts,
  finalizedEventDate,
  registryShippingAddress,
}: {
  deliverableProducts: number;
  finalizedEventDate: Maybe<DateFragment>;
  registryShippingAddress: ShippingAddressFragment | null;
}) => {
  const isShippingAddressValidated = registryShippingAddress?.validated;
  const hasShippingAddress = !!registryShippingAddress;
  if (deliverableProducts === 0) {
    return WarningLevel.None;
  }

  if (isTheWeddingSoon(finalizedEventDate)) {
    return WarningLevel.Severe;
  }

  // if shipping address exists but is not validated, return Severe warning, if not, check the products.
  if (hasShippingAddress && !isShippingAddressValidated) {
    return WarningLevel.Severe;
  }

  return deliverableProducts >= 10 ? WarningLevel.Moderate : WarningLevel.Low;
};

/**
 * **getReminderWarningLevel**:
 *
 * This function assumes we don't have shipping address. So always we will show a WarningLevel > 0
 * The condition to show the warnings is:
 *
 * - Low: Warning LOW: gifts > 0  and < 5.
 *
 * - Moderate: Warning Moderate: gifts >= 5 (in the previous warning, the gift limit is 10, here 5)
 *
 * - Severe: < 60 days to their wedding
 *
 * @param deliverableProducts number of deliverable products
 * @param finalizedEventDate wedding's date
 * @returns the corresponding warning level: None, Moderate or Severe
 */
export const getReminderWarningLevel = ({ deliverableProducts, finalizedEventDate }: { deliverableProducts: number; finalizedEventDate: Maybe<DateFragment> }) => {
  if (isTheWeddingSoon(finalizedEventDate)) {
    return WarningLevel.Severe;
  }
  return deliverableProducts >= 5 ? WarningLevel.Moderate : WarningLevel.Low;
};
